import { useState, useEffect } from "react";
import { GrossImage, Photo, Post } from "../../api/postAPI";
import {
  Slide,
  getGrossAnnotationList,
  getPhotoData,
  getSlideData,
  postAnnotation,
  postGrossAnnotation,
  postImageAnnotation,
} from "../../api/slideAPI";
import { getSlideMpp } from "../../api/deepzoomAPI";
import { OSDImageViewer, OSDSlideViewer } from "../viewer";
import { PostVideo, getVideoFileUrl } from "../../api/videoAPI";

interface Props {
  imageList: Photo[];
  slideList: Slide[];
  grossImageList: GrossImage[];
  currentIndex: number;
  isSlideFile: boolean;
  isGrossFile: boolean;
  currentImage: Photo | null;
  currentSlide: Slide;
  currentGrossImage: GrossImage | null;
  currentPost: Post;
  isAnno?: boolean;
  /** 동영상 탭이 선택되면 뷰어 자리에서 동영상을 재생한다 */
  isVideoFile?: boolean;
  currentVideo?: PostVideo | null;
}

const ViewerDisplay = ({
  isSlideFile,
  isGrossFile,
  currentImage,
  currentSlide,
  currentGrossImage,
  currentPost,
  isAnno = true,
  isVideoFile = false,
  currentVideo = null,
}: Props) => {
  const [slideState, setSlideState] = useState<Slide | null>(currentSlide);

  useEffect(() => {
    setSlideState(currentSlide);
  }, [currentSlide]);

  const handleSlideDoubleClick = () => {
    if (currentSlide?.filename) {
      window.open(
        `/viewer/${currentSlide.filename}?${currentPost?.id}`,
        "_blank"
      );
    }
  };

  const handleGetMpp = async (postId: number, filename: string) => {
    return await getSlideMpp(postId, filename);
  };

  const handleGetAnnotations = async (slideId: number) => {
    const data = await getSlideData(slideId);
    return { annotations: data.annotations, slideComments: data.slideComments };
  };

  const handleSaveAnnotation = async (slideId: number, path: any, name: string) => {
    await postAnnotation(slideId, path, name);
    const data = await getSlideData(slideId);
    setSlideState((prev) => (prev ? { ...prev, ...data } : data));
  };

  // 일반 이미지 어노테이션
  const handleGetImageAnnotations = async (photoId: number) => {
    const data = await getPhotoData(photoId);
    return Array.isArray(data) ? data : [];
  };

  const handleSaveImageAnnotation = async (photoId: number, path: any, name: string) => {
    await postImageAnnotation(photoId, path, name);
  };

  // Gross 이미지 어노테이션
  const handleGetGrossAnnotations = async (grossPhotoId: number) => {
    const data = await getGrossAnnotationList(grossPhotoId);
    return Array.isArray(data) ? data : [];
  };

  const handleSaveGrossAnnotation = async (grossPhotoId: number, path: any, name: string) => {
    await postGrossAnnotation(grossPhotoId, path, name);
  };

  const renderViewer = () => {
    if (isVideoFile) {
      return currentVideo ? (
        <video
          // 다른 동영상을 고르면 처음부터 다시 로드되도록 key 지정
          key={currentVideo.videoId}
          src={getVideoFileUrl(currentVideo)}
          controls
          playsInline
          preload="metadata"
          style={{ width: "100%", height: "100%", background: "#000", display: "block" }}
        />
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            color: "white",
          }}
        >
          No Video Selected
        </div>
      );
    }

    if (isSlideFile && currentSlide?.filename) {
      return (
        <OSDSlideViewer
          filename={currentSlide.filename}
          postId={currentPost.id}
          slideId={currentSlide.slideId}
          slideTitle={currentSlide?.slidename || ""}
          isAnno={isAnno}
          onDoubleClick={handleSlideDoubleClick}
          onGetSlideMpp={handleGetMpp}
          onGetAnnotations={handleGetAnnotations}
          onSaveAnnotation={handleSaveAnnotation}
          isReset={false}
          isUserSet={true}
          isSaveSet={false}
          externalSlide={slideState}
          onSlideChange={setSlideState}
        />
      );
    }

    if (isGrossFile && currentGrossImage?.pathFileName) {
      return (
        <OSDImageViewer
          imagePath={currentGrossImage.pathFileName}
          photoId={currentGrossImage.grossPhotoId}
          isGrossFile={true}
          isAnno={isAnno}
          isUserSet={true}
          onGetAnnotations={handleGetGrossAnnotations}
          onSaveAnnotation={handleSaveGrossAnnotation}
        />
      );
    }

    if (!isSlideFile && !isGrossFile && currentImage?.pathFileName) {
      return (
        <OSDImageViewer
          imagePath={currentImage.pathFileName}
          photoId={currentImage.photoId}
          isAnno={isAnno}
          isUserSet={true}
          onSaveAnnotation={handleSaveImageAnnotation}
          onGetAnnotations={handleGetImageAnnotations}
        />
      );
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          color: "white",
        }}
      >
        No Image Selected
      </div>
    );
  };

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {renderViewer()}
    </div>
  );
};

export default ViewerDisplay;